// Single source of truth for status badge colors.
//
// Policy (from the user): use the company theme in Redux wherever applicable;
// when a semantic color isn't in Redux, reuse the SAME hex values that already
// existed hardcoded in the codebase (don't invent new palettes for dark mode).
// These status chips are self-contained colored pills — the `bg` + `fg` pair
// maintains its own contrast regardless of the page background.

export type StatusKey =
  | "success" // green  — "Confirmed", "Assigned", etc.
  | "warning" // amber  — "Enquiry", "Testing", "Remind Later"
  | "error" // red    — "Cancelled", "View Venue" alert
  | "info" // blue   — "View Stream", neutral information
  | "fulfilled" // indigo — completion / high-priority / "Fulfilled"
  | "draft" // muted  — "Lost", idle states
  | "pending" // grey   — unknown states
  | "neutral"; // grey   — fallback

export interface StatusColor {
  bg: string;
  fg: string;
  border: string;
}

export type StatusColorMap = Record<StatusKey, StatusColor>;

// Values mirror the hex literals already present across the codebase
// (Notifications.tsx, Kanban.tsx, Checklists.tsx, ViewChecklist.tsx, etc.).
// Kept identical for light and dark mode — the chip itself carries bg+fg,
// so contrast is preserved on any page background.
const STATUS: StatusColorMap = {
  success: { bg: "#DCFCE7", fg: "#15803D", border: "#4BA663" },
  warning: { bg: "#F79E1B20", fg: "#F79E1B", border: "#F79E1B" },
  error: { bg: "#FEE2E2", fg: "#B91C1C", border: "#EB4335" },
  info: { bg: "#E0F2FE", fg: "#0369A1", border: "#0EA5E9" },
  fulfilled: { bg: "#0000FF26", fg: "#0000FF", border: "#0000FF" },
  draft: { bg: "#ECE2D6", fg: "#3B3B3B", border: "#BDBDBD" },
  pending: { bg: "#BDBDBD40", fg: "#BDBDBD", border: "#BDBDBD" },
  neutral: { bg: "#F3F4F6", fg: "#374151", border: "#D1D5DB" },
};

// Mode param kept for forward compatibility / call-site ergonomics, but the
// policy is: one palette, both modes. Revisit only if Redux grows dedicated
// semantic status colors.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getStatusColors = (_mode: "light" | "dark"): StatusColorMap =>
  STATUS;

export const getStatusColor = (
  _mode: "light" | "dark",
  key: StatusKey,
): StatusColor => STATUS[key];
